/**
 * MAIOR CAIXA INSCRITA numa forma do mapa — onde o conteúdo da sala cabe.
 *
 * Até a v2 a sala era um retângulo, então "onde ficam as bancadas" era a
 * própria sala. Na v3 o gestor desenha o contorno que quiser (`dados/forma.js`)
 * e o cabeçalho, as áreas, as bancadas e as peças continuam sendo retângulos
 * dispostos em grade (`planta.js`). Precisamos então da maior caixa
 * RETANGULAR que cabe DENTRO do contorno: é ela que `planta.js` recebe no
 * lugar da caixa envolvente.
 *
 * Por tipo de forma:
 *
 *   retângulo — a própria caixa (nada a calcular);
 *   elipse    — o retângulo inscrito de área máxima é largura/√2 × altura/√2,
 *               centrado (conta fechada, sem busca);
 *   polígono  — a forma é rasterizada numa grade e vale o maior retângulo de
 *               células inteiramente dentro (algoritmo do histograma, O(n·m)).
 *               A caixa devolvida vai de CENTRO a CENTRO de célula, nunca até
 *               a borda: como só o centro de cada célula é testado, esticar até
 *               a borda deixaria o conteúdo furar a parede num canto oblíquo.
 *
 * Numa sala em "L" o resultado é a maior perna do L — que é exatamente onde o
 * gestor tem espaço para as bancadas. O pedaço que sobra continua desenhado
 * (a parede segue o contorno), só não recebe conteúdo.
 *
 * Módulo puro: nada de DOM. Função barata o bastante para rodar por sala a
 * cada remontagem da planta (grade de 44×44 = 1936 testes de ponto), mas o
 * chamador deve memorizar por forma quando puder.
 */
import { LIMITES_FORMA, TIPO_FORMA, caixaEnvolvente, pontoDentro } from "./dados/forma";

/**
 * Células por lado da grade do polígono. 44 deixa a célula em ~2% do lado da
 * sala: a caixa inscrita perde uma célula de cada lado (o recuo de segurança)
 * e ainda fica com ~96% do vão disponível.
 */
export const RESOLUCAO_PADRAO = 44;

const RAIZ_DE_DOIS = Math.SQRT2;

// Cache da caixa inscrita de polígono (o mais antigo sai primeiro). 500
// contornos cobrem com folga salas, subsetores e postos de uma fábrica.
const LIMITE_CACHE = 500;
const CACHE_POLIGONO = new Map();

const caixaVazia = (caixa) => ({ x: caixa.x, y: caixa.y, largura: 0, altura: 0 });

/** Retângulo inscrito de área máxima na elipse: lados divididos por √2. */
export function caixaNaElipse(caixa) {
  const largura = caixa.largura / RAIZ_DE_DOIS;
  const altura = caixa.altura / RAIZ_DE_DOIS;
  return {
    x: caixa.x + (caixa.largura - largura) / 2,
    y: caixa.y + (caixa.altura - altura) / 2,
    largura,
    altura,
  };
}

// Uma célula "vale" quando o centro dela está dentro do contorno.
function mascaraDaForma(forma, caixa, colunas, linhas) {
  const larguraCelula = caixa.largura / colunas;
  const alturaCelula = caixa.altura / linhas;
  const mascara = new Uint8Array(colunas * linhas);
  for (let linha = 0; linha < linhas; linha += 1) {
    const y = caixa.y + (linha + 0.5) * alturaCelula;
    for (let coluna = 0; coluna < colunas; coluna += 1) {
      const x = caixa.x + (coluna + 0.5) * larguraCelula;
      if (pontoDentro(forma, { x, y })) mascara[linha * colunas + coluna] = 1;
    }
  }
  return mascara;
}

// Maior retângulo sob um histograma de alturas (pilha de índices crescentes).
function maiorNoHistograma(alturas, linhaDeBaixo) {
  const pilha = [];
  const total = alturas.length;
  let melhor = null;
  for (let coluna = 0; coluna <= total; coluna += 1) {
    const altura = coluna < total ? alturas[coluna] : 0;
    while (pilha.length > 0 && alturas[pilha[pilha.length - 1]] >= altura) {
      const topo = pilha.pop();
      const alturaTopo = alturas[topo];
      const esquerda = pilha.length > 0 ? pilha[pilha.length - 1] + 1 : 0;
      const largura = coluna - esquerda;
      const area = alturaTopo * largura;
      if (alturaTopo > 0 && (!melhor || area > melhor.area)) {
        melhor = {
          area,
          coluna: esquerda,
          colunas: largura,
          linha: linhaDeBaixo - alturaTopo + 1,
          linhas: alturaTopo,
        };
      }
    }
    pilha.push(coluna);
  }
  return melhor;
}

/**
 * Maior retângulo de células marcadas na máscara (1 = dentro), em CÉLULAS:
 * `{coluna, linha, colunas, linhas, area}` — `null` se nenhuma célula vale.
 * Exportado para o teste poder cobrir o algoritmo sem passar por uma forma.
 */
export function maiorRetanguloNaMascara(mascara, colunas, linhas) {
  const alturas = new Array(colunas).fill(0);
  let melhor = null;
  for (let linha = 0; linha < linhas; linha += 1) {
    for (let coluna = 0; coluna < colunas; coluna += 1) {
      const dentro = mascara[linha * colunas + coluna];
      alturas[coluna] = dentro ? alturas[coluna] + 1 : 0;
    }
    const daLinha = maiorNoHistograma(alturas, linha);
    if (daLinha && (!melhor || daLinha.area > melhor.area)) melhor = daLinha;
  }
  return melhor;
}

function caixaNoPoligono(forma, caixa, resolucao) {
  const colunas = Math.max(2, Math.round(resolucao));
  const linhas = colunas;
  const mascara = mascaraDaForma(forma, caixa, colunas, linhas);
  const melhor = maiorRetanguloNaMascara(mascara, colunas, linhas);
  if (!melhor) return caixaVazia(caixa);
  const larguraCelula = caixa.largura / colunas;
  const alturaCelula = caixa.altura / linhas;
  // De centro a centro: é o único trecho que a amostragem PROVOU estar dentro.
  return {
    x: caixa.x + (melhor.coluna + 0.5) * larguraCelula,
    y: caixa.y + (melhor.linha + 0.5) * alturaCelula,
    largura: Math.max(0, (melhor.colunas - 1) * larguraCelula),
    altura: Math.max(0, (melhor.linhas - 1) * alturaCelula),
  };
}

/**
 * `{x, y, largura, altura}` da maior caixa que cabe dentro da forma. Aceita
 * também uma caixa crua (elemento do layout da v2), que devolve a si mesma.
 *
 * Pode devolver caixa pequena (ou de lado zero) quando o contorno é estreito
 * demais: quem desenha trata isso mostrando a sala compacta, em vez de fingir
 * que cabe conteúdo ali.
 */
export function caixaInscrita(forma, { resolucao = RESOLUCAO_PADRAO } = {}) {
  const caixa = caixaEnvolvente(forma);
  if (!caixa) return null;
  if (caixa.largura <= 0 || caixa.altura <= 0) return caixaVazia(caixa);
  if (forma?.tipo === TIPO_FORMA.ELIPSE) return caixaNaElipse(caixa);
  if (forma?.tipo !== TIPO_FORMA.POLIGONO) return caixa;
  const pontos = Array.isArray(forma.pontos) ? forma.pontos : [];
  if (pontos.length < LIMITES_FORMA.PONTOS_MINIMO) return caixa;
  // O polígono é rasterizado (44×44 testes de ponto) e a planta desenhada o
  // pede a cada escala tentada e a cada leitura do estado: o mesmo contorno
  // dá sempre a mesma caixa, então ela é guardada.
  const chave = `${resolucao}|${pontos.map((p) => `${p.x},${p.y}`).join(";")}`;
  const guardada = CACHE_POLIGONO.get(chave);
  if (guardada) return { ...guardada };
  const calculada = caixaNoPoligono(forma, caixa, resolucao);
  if (CACHE_POLIGONO.size >= LIMITE_CACHE) CACHE_POLIGONO.delete(CACHE_POLIGONO.keys().next().value);
  CACHE_POLIGONO.set(chave, calculada);
  return { ...calculada };
}

const dentroDoRetangulo = (r, p, folga) =>
  p.x >= r.x - folga && p.x <= r.x + r.largura + folga && p.y >= r.y - folga && p.y <= r.y + r.altura + folga;

/**
 * Maior retângulo LIVRE dentro de `caixa`: fora de todos os `obstaculos`
 * (retângulos, afastados de `folga`) e, com `dentro`, também dentro daquela
 * forma. É onde o mapa geral põe o cabeçalho e a Fila de uma sala cujo
 * interior o gestor desenhou à mão — o espaço que os subsetores dele não
 * ocupam. Mesmo algoritmo da caixa inscrita (grade + histograma), e a mesma
 * régua de centro a centro de célula.
 *
 * `null` quando não sobra nada.
 */
export function maiorEspacoLivre(caixa, obstaculos = [], { dentro = null, folga = 0, resolucao = RESOLUCAO_PADRAO } = {}) {
  if (!caixa || caixa.largura <= 0 || caixa.altura <= 0) return null;
  const colunas = Math.max(2, Math.round(resolucao));
  const linhas = colunas;
  const larguraCelula = caixa.largura / colunas;
  const alturaCelula = caixa.altura / linhas;
  const mascara = new Uint8Array(colunas * linhas);
  for (let linha = 0; linha < linhas; linha += 1) {
    const y = caixa.y + (linha + 0.5) * alturaCelula;
    for (let coluna = 0; coluna < colunas; coluna += 1) {
      const p = { x: caixa.x + (coluna + 0.5) * larguraCelula, y };
      const livre = (!dentro || pontoDentro(dentro, p)) && !obstaculos.some((r) => dentroDoRetangulo(r, p, folga));
      if (livre) mascara[linha * colunas + coluna] = 1;
    }
  }
  const melhor = maiorRetanguloNaMascara(mascara, colunas, linhas);
  if (!melhor) return null;
  // Sem obstáculo nem contorno, a célula inteira é livre: a caixa toda vale.
  // Com eles, só o trecho de centro a centro foi provado livre.
  if (!dentro && obstaculos.length === 0) return { ...caixa };
  return {
    x: caixa.x + (melhor.coluna + 0.5) * larguraCelula,
    y: caixa.y + (melhor.linha + 0.5) * alturaCelula,
    largura: Math.max(0, (melhor.colunas - 1) * larguraCelula),
    altura: Math.max(0, (melhor.linhas - 1) * alturaCelula),
  };
}

/** A caixa inscrita tem espaço útil (não é sliver nem caixa de lado zero). */
export function temEspacoUtil(caixa, minimo = LIMITES_FORMA.LADO_MINIMO) {
  return Boolean(caixa) && caixa.largura >= minimo && caixa.altura >= minimo;
}
